import { FC, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { fetchSites, Site } from "../../../redux/slices/sitesSlice";
import { openSnackbar } from "../../../redux/slices/snackbarSlice";
import {
  clearSageRepresentants,
  createRepresentant,
  SageRepresentant,
  searchSageRepresentants,
} from "../slices/representantSitesSlice";
import SitesMultiSelect from "./SitesMultiSelect";

interface RepresentantCreateDialogProps {
  open: boolean;
  onClose: () => void;
}

const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_MIN_LENGTH = 2;
// Politique de mot de passe d'Identity côté API : 6 caractères minimum.
const PASSWORD_MIN_LENGTH = 6;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getSageLabel = (option: SageRepresentant | string) =>
  typeof option === "string"
    ? option
    : option.nom
    ? `${option.code} - ${option.nom}`
    : option.code;

/** Création d'un compte représentant par l'administrateur, avec code Sage et sites optionnels. */
const RepresentantCreateDialog: FC<RepresentantCreateDialogProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const direction = useAppSelector((state) => state.languageSlice.direction);
  const { sites, loading: sitesLoading } = useAppSelector(
    (state) => state.sitesSlice
  );
  const { creating, sageRepresentants, sageLoading } = useAppSelector(
    (state) => state.representantSitesSlice
  );

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [sageRepresentant, setSageRepresentant] = useState<SageRepresentant | string | null>(null);
  const [sageSearch, setSageSearch] = useState("");
  const [sitesVente, setSitesVente] = useState<Site[]>([]);
  const [sitesExpedition, setSitesExpedition] = useState<Site[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const allSites = useMemo(() => sites || [], [sites]);

  const businessId = useMemo(() => {
    if (!sageRepresentant) return "";
    return typeof sageRepresentant === "string"
      ? sageRepresentant.trim()
      : sageRepresentant.code;
  }, [sageRepresentant]);

  useEffect(() => {
    if (open && !sites && !sitesLoading) {
      dispatch(fetchSites());
    }
  }, [dispatch, open, sites, sitesLoading]);

  // Réinitialisation du formulaire à chaque ouverture.
  useEffect(() => {
    if (open) {
      setUsername("");
      setPassword("");
      setConfirmPassword("");
      setEmail("");
      setSageRepresentant(null);
      setSageSearch("");
      setSitesVente([]);
      setSitesExpedition([]);
      setSubmitted(false);
      dispatch(clearSageRepresentants());
    }
  }, [open, dispatch]);

  // Recherche Sage avec anti-rebond sur la saisie.
  useEffect(() => {
    if (!open) return;
    const term = sageSearch.trim();
    if (term.length < SEARCH_MIN_LENGTH) return;
    const handle = setTimeout(() => {
      dispatch(searchSageRepresentants(term));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [open, sageSearch, dispatch]);

  const passwordMismatch = confirmPassword.length > 0 && password !== confirmPassword;
  const passwordTooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const emailInvalid = email.trim().length > 0 && !EMAIL_PATTERN.test(email.trim());
  const usernameError = submitted && username.trim().length === 0;
  const passwordError = passwordTooShort || (submitted && password.length === 0);
  const confirmError = passwordMismatch || (submitted && confirmPassword.length === 0);
  const emailError = emailInvalid || (submitted && email.trim().length === 0);
  const businessIdError = submitted && businessId.length === 0;

  const isValid =
    username.trim().length > 0 &&
    password.length >= PASSWORD_MIN_LENGTH &&
    password === confirmPassword &&
    email.trim().length > 0 &&
    !emailInvalid &&
    businessId.length > 0;

  const notify = (message: string, type: "success" | "error") =>
    dispatch(
      openSnackbar({
        message,
        type,
        horizontal: direction === "rtl" ? "left" : "right",
      })
    );

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!isValid) return;
    try {
      await dispatch(
        createRepresentant({
          username: username.trim(),
          password,
          email: email.trim(),
          businessId,
          sitesVente: sitesVente.map((site) => site.Code),
          sitesExpedition: sitesExpedition.map((site) => site.Code),
        })
      ).unwrap();
      notify(t("administration.representantSites.created"), "success");
      onClose();
    } catch (error) {
      notify(
        typeof error === "string"
          ? error
          : t("administration.representantSites.createError"),
        "error"
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : onClose}
      fullWidth
      maxWidth="md"
      dir={direction}
      aria-labelledby="representant-create-dialog-title"
    >
      <DialogTitle id="representant-create-dialog-title">
        {t("administration.representantSites.createTitle")}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container columnSpacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("administration.representantSites.username")}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              error={usernameError}
              helperText={usernameError ? t("administration.representantSites.required") : " "}
              fullWidth
              margin="normal"
              autoComplete="off"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("administration.representantSites.email")}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={emailError}
              helperText={
                emailInvalid
                  ? t("administration.representantSites.emailInvalid")
                  : emailError
                  ? t("administration.representantSites.required")
                  : " "
              }
              fullWidth
              margin="normal"
              autoComplete="off"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("administration.representantSites.password")}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={passwordError}
              helperText={
                passwordTooShort
                  ? t("administration.representantSites.passwordTooShort", { min: PASSWORD_MIN_LENGTH })
                  : passwordError
                  ? t("administration.representantSites.required")
                  : " "
              }
              fullWidth
              margin="normal"
              autoComplete="new-password"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("administration.representantSites.confirmPassword")}
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={confirmError}
              helperText={
                passwordMismatch
                  ? t("administration.representantSites.passwordMismatch")
                  : confirmError
                  ? t("administration.representantSites.required")
                  : " "
              }
              fullWidth
              margin="normal"
              autoComplete="new-password"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              id="sageRepresentant"
              freeSolo
              options={sageRepresentants}
              value={sageRepresentant}
              loading={sageLoading}
              inputValue={sageSearch}
              onInputChange={(_, newInput) => setSageSearch(newInput)}
              onChange={(_, newValue) => setSageRepresentant(newValue)}
              getOptionLabel={getSageLabel}
              isOptionEqualToValue={(option, selected) =>
                typeof selected !== "string" && option.code === selected.code
              }
              filterOptions={(options) => options}
              noOptionsText={
                sageSearch.trim().length < SEARCH_MIN_LENGTH
                  ? t("administration.representantSites.sageSearchHint")
                  : t("administration.representantSites.noOption")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("administration.representantSites.sageRepresentant")}
                  margin="normal"
                  required
                  error={businessIdError}
                  helperText={
                    businessIdError
                      ? t("administration.representantSites.required")
                      : businessId
                      ? `${t("administration.representantSites.columns.code")} : ${businessId}`
                      : t("administration.representantSites.sageSearchHint")
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {sageLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SitesMultiSelect
              id="createSitesVente"
              label={t("administration.representantSites.salesSites")}
              options={allSites}
              value={sitesVente}
              loading={sitesLoading}
              onChange={setSitesVente}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SitesMultiSelect
              id="createSitesExpedition"
              label={t("administration.representantSites.expSites")}
              options={allSites}
              value={sitesExpedition}
              loading={sitesLoading}
              onChange={setSitesExpedition}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={onClose}
            disabled={creating}
            sx={{ color: "#fff", backgroundColor: "red", width: "fit-content" }}
          >
            {t("administration.representantSites.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={creating}
            variant="contained"
            color="primary"
            sx={{ width: "fit-content" }}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t("administration.representantSites.create")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default RepresentantCreateDialog;
